/**
 * EffectBench (W3-04 effects-fidelity; ARCHITECTURE.md §7.3 step 5, formats-and-packaging.md P§5.2): runs one
 * extra.json effect over a still image with the preview's own effect runner (`EffectRunner`, the same ping-pong
 * targets, vertex stage, uniform order and precision injection), on its own WebGL2 context, and reads the result
 * back. The `#/dev/effects` comparer (Appendix C H5) and the headless-Chromium tests use it.
 *
 * Images are RGBA8 with the top row first (ImageData layout). The input is uploaded flipped, so the texture's first
 * row is the image's bottom, exactly how the preview stores its frames (`uv` (0, 0) = bottom-left).
 */

import type { EffectDraw } from '../../../../core/engine/types';
import type { AssetId, BuiltinShader, EffectId } from '../../../../core/model/types';
import { GlState, type PxRect } from '../../../gl/glState';
import { QuadBatch } from '../../../gl/quadBatch';
import { blitColor, PingPong } from '../../../gl/renderTargets';
import type { BuiltinShaderDef, TextureProvider } from '../../../types';
import { BUILTIN_SHADERS } from '../builtin/index';
import { EffectRunner, type EffectFrameReport } from '../runner';

/** RGBA8 pixels, top row first (the layout of ImageData). */
export interface RgbaImage {
  readonly width: number;
  readonly height: number;
  readonly data: Uint8ClampedArray;
}

/** The shader of a bench run: a built-in by name, or a custom fragment source (run like a package shader). */
export type BenchShader =
  { readonly kind: 'builtin'; readonly name: BuiltinShader } | { readonly kind: 'custom'; readonly source: string };

export interface BenchRun {
  readonly shader: BenchShader;
  /** Uniform values as the engine evaluates them (colours already 0..1), applied after the shader defaults. */
  readonly vars: ReadonlyArray<readonly [name: string, value: number | readonly number[]]>;
  /** Chart seconds (uniform `time`). */
  readonly time: number;
  /** Null: a global effect over the whole image. Otherwise the chart viewport inside the image (px, y down). */
  readonly viewport: PxRect | null;
}

export interface BenchResult {
  readonly image: RgbaImage;
  readonly report: EffectFrameReport;
}

/** The asset id the bench gives a custom shader. */
const CUSTOM_ASSET = 'bench-shader' as AssetId;
const EFFECT_ID = 1 as EffectId;

/** The letterboxed viewport of a chart aspect ratio inside a w x h image (centred, Phira's rounding; R§1.2). */
export function letterbox(w: number, h: number, aspect: number): PxRect {
  const fitW = h * aspect;
  const vw = Math.trunc(fitW > w ? w : fitW);
  const vh = Math.trunc(fitW > w ? w / aspect : h);
  return { x: Math.round((w - vw) / 2), y: Math.round((h - vh) / 2), w: vw, h: vh };
}

/** A provider that knows one custom shader source and nothing else. */
function shaderProvider(source: () => string | null): TextureProvider {
  return {
    image: () => null,
    gif: () => null,
    blurredIllustration: () => null,
    shaderSource: (a) => (a === CUSTOM_ASSET ? source() : null),
    video: () => null,
    onReady: () => () => undefined,
    dispose: () => undefined,
  };
}

export class EffectBench {
  readonly gl: WebGL2RenderingContext;
  private readonly state: GlState;
  private readonly batch: QuadBatch;
  private readonly runner: EffectRunner;
  private readonly pp: PingPong;
  private readonly inputTex: WebGLTexture | null;
  private readonly inputFbo: WebGLFramebuffer | null;
  private custom: string | null = null;
  private readonly provider: TextureProvider;
  private w = 0;
  private h = 0;
  private disposed = false;

  private constructor(gl: WebGL2RenderingContext, builtins: () => readonly BuiltinShaderDef[]) {
    this.gl = gl;
    this.state = new GlState(gl);
    this.batch = new QuadBatch(gl, this.state);
    this.runner = new EffectRunner(gl, this.state, this.batch, builtins);
    this.pp = new PingPong(gl);
    this.inputTex = gl.createTexture();
    this.inputFbo = gl.createFramebuffer();
    this.provider = shaderProvider(() => this.custom);
  }

  /**
   * A bench on `canvas` (a fresh offscreen canvas by default), or null when WebGL2 is unavailable. `builtins` replaces
   * the built-in shader list (tests).
   */
  static create(
    canvas: HTMLCanvasElement | OffscreenCanvas = new OffscreenCanvas(1, 1),
    builtins: () => readonly BuiltinShaderDef[] = () => BUILTIN_SHADERS,
  ): EffectBench | null {
    const attrs: WebGLContextAttributes = {
      alpha: false,
      premultipliedAlpha: false,
      antialias: false,
      preserveDrawingBuffer: false,
    };
    const gl =
      canvas instanceof OffscreenCanvas ? canvas.getContext('webgl2', attrs) : canvas.getContext('webgl2', attrs);
    return gl === null ? null : new EffectBench(gl, builtins);
  }

  /** Size of the current input (0 x 0 before the first `setInput`). */
  get size(): { readonly w: number; readonly h: number } {
    return { w: this.w, h: this.h };
  }

  /** Uploads the image every following run starts from. */
  setInput(img: RgbaImage): void {
    const gl = this.gl;
    this.w = Math.max(1, img.width);
    this.h = Math.max(1, img.height);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.inputTex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    const pixels = new Uint8Array(img.data.buffer, img.data.byteOffset, img.width * img.height * 4);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, img.width, img.height, 0, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.inputFbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.inputTex, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    this.state.forgetTarget();
    this.pp.ensure(this.w, this.h);
  }

  /** Runs one effect over the input and reads the result back (the input itself when the effect did not run). */
  run(r: BenchRun): BenchResult {
    if (this.disposed) throw new Error('EffectBench: disposed');
    const gl = this.gl;
    const { w, h } = this;
    this.runner.beginFrame();
    this.state.reset();
    blitColor(gl, this.inputFbo, this.pp.a.fbo, w, h);
    this.state.forgetTarget();
    this.custom = r.shader.kind === 'custom' ? r.shader.source : null;
    const draw: EffectDraw = {
      effect: EFFECT_ID,
      shader: r.shader.kind === 'builtin' ? r.shader : { kind: 'asset', asset: CUSTOM_ASSET },
      uniforms: r.vars,
      time: r.time,
      global: r.viewport === null,
    };
    const out = this.runner.chain(
      [draw],
      this.pp.a,
      this.pp,
      { targetW: w, targetH: h, viewport: r.viewport },
      this.provider,
    );
    this.batch.flush();
    return { image: this.read(out.fbo), report: this.runner.frameReport };
  }

  /** The current input as the bench stores it (a round trip through the GPU). */
  readInput(): RgbaImage {
    return this.read(this.inputFbo);
  }

  private read(fbo: WebGLFramebuffer | null): RgbaImage {
    const gl = this.gl;
    const { w, h } = this;
    const raw = new Uint8Array(w * h * 4);
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbo);
    gl.readPixels(0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, raw);
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
    this.state.forgetTarget();
    // GL rows run bottom-up; images top-down.
    const data = new Uint8ClampedArray(w * h * 4);
    const row = w * 4;
    for (let y = 0; y < h; y++) data.set(raw.subarray((h - 1 - y) * row, (h - y) * row), y * row);
    return { width: w, height: h, data };
  }

  /**
   * Frees the GL objects and releases the WebGL context itself (WEBGL_lose_context), so pages that mount a bench
   * again and again (the comparer route) do not pile up live contexts until garbage collection.
   */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.runner.dispose();
    this.pp.dispose();
    this.batch.dispose();
    this.gl.deleteTexture(this.inputTex);
    this.gl.deleteFramebuffer(this.inputFbo);
    this.gl.getExtension('WEBGL_lose_context')?.loseContext();
  }
}
